import { useMemo, useState } from "react";
import { LifeBuoy, Search } from "lucide-react";
import { roleLabels } from "../api";

/* ----------------------------------------------------------------------
   Rupee Xpert — in-app help / user guide.
   Each section is a self-contained block so we can filter by text search
   and highlight the sections most relevant to the current user's role.
----------------------------------------------------------------------- */

const roleHighlights = {
  admin: ["admin-panel", "reports", "audit", "digest", "rbac"],
  business_head: ["dashboard", "reports", "audit", "rbac"],
  sales_manager: ["dashboard", "leads-sales", "followups", "reports", "rbac"],
  telecaller: ["leads-sales", "followups", "documents", "customer-link", "rbac"],
  back_office_manager: ["leads-boe", "followups", "reports", "digest", "rbac"],
  back_office_executive: ["leads-boe", "followups", "documents", "rbac"],
};

const SECTIONS = [
  {
    id: "getting-started",
    title: "1. Getting started",
    body: (
      <>
        <p>Rupee Xpert is a Loan DSA CRM with two parallel hierarchies:</p>
        <ul>
          <li><strong>Sales</strong> — Business Head → Sales Manager → Telecaller</li>
          <li><strong>Back Office</strong> — Business Head → Back Office Manager → Back Office Executive</li>
        </ul>
        <p>
          Sales brings the lead in. Back Office processes it with a bank/NBFC.
          Every action is scope-checked and every edit is audited with a mandatory reason.
        </p>
        <p className="muted">Live site: <a href="https://rupeexpert.in" target="_blank" rel="noreferrer">rupeexpert.in</a></p>
      </>
    ),
  },
  {
    id: "login",
    title: "2. Logging in",
    body: (
      <>
        <ol>
          <li>Open <a href="https://rupeexpert.in" target="_blank" rel="noreferrer">rupeexpert.in</a>.</li>
          <li>Enter your work email + the password your admin gave you.</li>
          <li>Click <strong>Enter workspace</strong>.</li>
        </ol>
        <p className="callout">
          <strong>Locked out?</strong> 5 wrong attempts inside 15 minutes locks that email temporarily.
          Ask an admin to clear it.
        </p>
      </>
    ),
  },
  {
    id: "dashboard",
    title: "3. Home / Dashboard",
    body: (
      <>
        <p>Everyone lands on a role-scoped dashboard.</p>
        <ul>
          <li><strong>Telecaller</strong> — your leads, your follow-ups due today, your conversion rate.</li>
          <li><strong>Sales Manager</strong> — team totals, top telecallers, funnel by status.</li>
          <li><strong>BOM / BOE</strong> — leads in your queue, idle leads, disbursement pipeline.</li>
          <li><strong>Admin</strong> — global KPIs, bank load board, routing audit.</li>
        </ul>
        <p>Every KPI card is clickable and drills into a pre-filtered list.</p>
      </>
    ),
  },
  {
    id: "leads-sales",
    title: "4. Creating a lead (Sales)",
    body: (
      <>
        <ol>
          <li>Open <strong>Login Status</strong> → <strong>Add lead</strong>.</li>
          <li>Fill customer name, mobile, PAN, loan type, requested amount.</li>
          <li>Save. The lead is assigned to the telecaller who created it.</li>
        </ol>
        <p>
          Once a <strong>Bank/NBFC</strong> is chosen (by admin or Back Office), the lead is
          <strong> auto-routed</strong> to the specific BOE who handles that bank. There is no round-robin —
          the mapping is fixed <strong>1 bank ↔ 1 BOE</strong>.
        </p>
        <p className="callout">
          After saving, Telecallers &amp; Sales Managers <strong>cannot edit lead fields</strong>.
          They can still add follow-ups, upload documents, share the customer upload link, and view the audit history.
        </p>
      </>
    ),
  },
  {
    id: "leads-boe",
    title: "5. Processing a lead (Back Office)",
    body: (
      <>
        <p>Every lead routed to a BOE lands in their <strong>Login Status</strong> queue.</p>
        <p>Back Office roles can change <strong>only three fields</strong>:</p>
        <ul>
          <li><strong>Bank / NBFC</strong> — changing this may re-route the lead to a different BOE.</li>
          <li><strong>Loan amount</strong> (updated/sanctioned).</li>
          <li><strong>Status</strong> — e.g. Docs pending → Sent to bank → Approved → Disbursed.</li>
        </ul>
        <p>
          <strong>Every change asks for a reason</strong> before saving. That reason plus the
          old value → new value is written to the lead's audit trail.
        </p>
        <p><strong>Bulk status change:</strong> select multiple leads via checkboxes → <em>Change status</em> → new status → <strong>enter a reason</strong> → confirm.</p>
      </>
    ),
  },
  {
    id: "followups",
    title: "6. Follow-ups",
    body: (
      <>
        <p>Anyone who can view a lead can add follow-ups.</p>
        <ul>
          <li>Pick a due date/time, type (Call / WhatsApp / Meeting / Email), and notes.</li>
          <li>Overdue and due-today follow-ups surface on the dashboard, in the Morning Digest email, and in Reports.</li>
          <li>Close a follow-up by writing an <strong>Outcome</strong>. It stops appearing as pending.</li>
        </ul>
      </>
    ),
  },
  {
    id: "customer-link",
    title: "7. Customer upload link",
    body: (
      <>
        <p>Generate a secure, expiring public URL so the customer can upload KYC/income documents without logging in.</p>
        <ol>
          <li>Open a lead → <strong>Documents</strong> → <strong>Generate customer link</strong>.</li>
          <li>Copy the URL and send it via WhatsApp/SMS.</li>
          <li>Customer opens the branded <em>Rupee Xpert · Secure Upload</em> page and uploads files.</li>
          <li>Uploaded documents attach back to the lead automatically.</li>
        </ol>
        <p className="muted">Links can be revoked at any time from the same section.</p>
      </>
    ),
  },
  {
    id: "audit",
    title: "8. Audit trail",
    body: (
      <>
        <p>Every lead has an <strong>Audit</strong> tab showing:</p>
        <ul>
          <li><strong>Who</strong> made the change (name + role)</li>
          <li><strong>When</strong> it happened</li>
          <li><strong>What</strong> changed — old value → new value, field by field</li>
          <li><strong>Why</strong> — the mandatory reason typed by the user</li>
        </ul>
        <p>Admin sees audit for every lead. Managers see audit for leads in their scope.</p>
      </>
    ),
  },
  {
    id: "reports",
    title: "9. Reports & saved views",
    body: (
      <>
        <p>Slice data by status, bank, loan type, team, telecaller, BOE, or date range.</p>
        <ul>
          <li><strong>Saved views</strong> — save a filter combination as a named view; toggle <em>Team-shared</em> to make it visible to your teammates.</li>
          <li><strong>Export CSV</strong> — admin + business head.</li>
          <li><strong>Routing audit</strong> (admin) — every routing decision, so you can audit fairness.</li>
          <li><strong>Bank Load Board</strong> (admin) — live workload per bank + BOE, with idle alerts.</li>
        </ul>
      </>
    ),
  },
  {
    id: "digest",
    title: "10. Morning Digest email",
    body: (
      <>
        <p>Every day at <strong>09:30 IST</strong> every <strong>Sales Manager</strong> and <strong>Back Office Manager</strong> receives an email summarising:</p>
        <ul>
          <li>Overdue follow-ups</li>
          <li>Follow-ups due today</li>
          <li>Idle leads — open status with no update for 3+ days</li>
        </ul>
        <p>Admin can preview and manually re-send from <strong>Admin → Morning digest</strong>.</p>
        <p className="callout">For real delivery in production, admin must paste a Resend API key + a verified sender email in the production deployment's environment variables.</p>
      </>
    ),
  },
  {
    id: "admin-panel",
    title: "11. Admin Panel",
    body: (
      <>
        <p>Only Admins see the <strong>Settings</strong> item in the sidebar. It contains:</p>
        <ul>
          <li><strong>Users</strong> — create / deactivate / delete, assign role + team, tick per-permission overrides.</li>
          <li><strong>Teams</strong> — Sales teams and Back Office teams.</li>
          <li><strong>Banks &amp; Products</strong> — partner banks/NBFCs and loan products. Each bank is mapped to exactly one BOE (this drives auto-routing).</li>
          <li><strong>Statuses</strong> — customise status labels shown in dropdowns.</li>
          <li><strong>Bank load board</strong> — live per-bank workload.</li>
          <li><strong>Morning digest</strong> — preview + send now.</li>
        </ul>
        <p><strong>Manual BOE override:</strong> on any lead, use <em>Reassign BOE</em> → pick a different BOE + reason. Both the override and the reason are captured in the audit trail.</p>
      </>
    ),
  },
  {
    id: "shortcuts",
    title: "12. Keyboard tips",
    body: (
      <ul>
        <li><kbd>/</kbd> — jump to global search</li>
        <li><kbd>Esc</kbd> — close any open modal</li>
        <li><kbd>Ctrl</kbd>/<kbd>⌘</kbd> + <kbd>K</kbd> — command palette</li>
      </ul>
    ),
  },
  {
    id: "troubleshooting",
    title: "13. Troubleshooting",
    body: (
      <div className="table-wrap" style={{ marginTop: 4 }}>
        <table>
          <thead>
            <tr><th>Symptom</th><th>Likely cause / fix</th></tr>
          </thead>
          <tbody>
            <tr><td>"Only admins can…" 403</td><td>Your role doesn't allow that action. Ask an admin.</td></tr>
            <tr><td>"A reason is required"</td><td>Every edit needs a reason. Type one and try again.</td></tr>
            <tr><td>"Too many attempts" on login</td><td>5 failed logins in 15 min. Wait, or ask admin to clear it.</td></tr>
            <tr><td>Customer upload link says expired</td><td>Regenerate from the lead's Documents section.</td></tr>
            <tr><td>Digest email not received</td><td>Only Sales Managers + BOMs receive it. Confirm the Resend key is set on the production deployment.</td></tr>
            <tr><td>Lead didn't route to a BOE</td><td>The bank on the lead isn't mapped to a BOE. Admin → Banks &amp; Products → set the BOE.</td></tr>
          </tbody>
        </table>
      </div>
    ),
  },
  {
    id: "rbac",
    title: "14. Roles at a glance (RBAC)",
    body: (
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Role</th><th>View</th><th>Create</th><th>Edit any</th>
              <th>Edit Bank/Amount/Status</th><th>Delete</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Admin</td><td>All</td><td>Yes</td><td>Yes</td><td>Yes</td><td>Yes</td></tr>
            <tr><td>Business Head</td><td>All</td><td>Yes</td><td>—</td><td>—</td><td>—</td></tr>
            <tr><td>Sales Manager</td><td>Team</td><td>Yes</td><td>—</td><td>—</td><td>—</td></tr>
            <tr><td>Telecaller</td><td>Own</td><td>Yes</td><td>—</td><td>—</td><td>—</td></tr>
            <tr><td>Back Office Manager</td><td>Team</td><td>—</td><td>—</td><td>Yes (with reason)</td><td>—</td></tr>
            <tr><td>Back Office Executive</td><td>Own</td><td>—</td><td>—</td><td>Yes (with reason)</td><td>—</td></tr>
          </tbody>
        </table>
        <p className="muted" style={{ marginTop: 10 }}>
          "Team" = leads inside your Sales/Back-office team. "Own" = leads assigned to you specifically.
          Every change to Bank / Amount / Status requires a written reason that lands in the audit log.
        </p>
      </div>
    ),
  },
];

export default function Help({ user }) {
  const [query, setQuery] = useState("");
  const highlighted = new Set(roleHighlights[user?.role] || []);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return SECTIONS;
    return SECTIONS.filter((s) => {
      const text = (s.title + " " + extractText(s.body)).toLowerCase();
      return text.includes(q);
    });
  }, [query]);

  return (
    <div className="help-page" data-testid="help-page">
      <div className="page-intro">
        <div>
          <p className="eyebrow">SUPPORT / USER GUIDE</p>
          <h1>How to use Rupee Xpert</h1>
          <p className="muted" style={{ maxWidth: 620 }}>
            Signed in as <strong>{user?.name}</strong> ({roleLabels[user?.role] || user?.role}).
            The sections marked <span className="help-tag">for you</span> are the ones that matter most for your role.
          </p>
        </div>
        <div className="help-search">
          <Search size={14} />
          <input
            data-testid="help-search"
            placeholder="Search the guide…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </div>

      <div className="help-layout">
        <aside className="help-toc">
          <p className="eyebrow" style={{ marginBottom: 12 }}>ON THIS PAGE</p>
          <nav>
            {visible.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className={highlighted.has(s.id) ? "help-toc-item highlighted" : "help-toc-item"}
              >
                {s.title}
                {highlighted.has(s.id) && <span className="help-tag">for you</span>}
              </a>
            ))}
            {visible.length === 0 && <p className="muted">Nothing matches "{query}"</p>}
          </nav>
          <div className="help-support">
            <LifeBuoy size={16} />
            <div>
              <strong>Still stuck?</strong>
              <small>Contact your workspace admin.</small>
            </div>
          </div>
        </aside>

        <main className="help-content">
          {visible.map((s) => (
            <section
              key={s.id}
              id={s.id}
              className={highlighted.has(s.id) ? "help-section highlighted" : "help-section"}
              data-testid={`help-section-${s.id}`}
            >
              <div className="help-section-head">
                <h2>{s.title}</h2>
                {highlighted.has(s.id) && <span className="help-tag">for you</span>}
              </div>
              <div className="help-section-body">{s.body}</div>
            </section>
          ))}
        </main>
      </div>
    </div>
  );
}

/* Best-effort text extraction from a React node for the search filter. */
function extractText(node) {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(extractText).join(" ");
  if (node.props?.children) return extractText(node.props.children);
  return "";
}
